"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  FileText,
  Code,
  ShoppingBag,
  Mail,
  Scale,
  Info,
} from "lucide-react";
import { cn } from "@/lib/utils";

// Navigation items
// Order: More | Verification | Receipts | API | Shopify (right to left in RTL)
const navItems = [
  {
    id: "invoices",
    icon: FileText,
    label: "קבלות",
    href: "/",
    comingSoon: false,
  },
  { id: "api", icon: Code, label: "API", href: "/api", comingSoon: false },
  {
    id: "shopify",
    icon: ShoppingBag,
    label: "Shopify",
    href: "/shopify",
    comingSoon: false,
  },
  {
    id: "contact",
    icon: Mail,
    label: "צור קשר",
    href: "/contact",
    comingSoon: false,
  },
];

// Secondary navigation items - desktop sidebar only
const secondaryNavItems = [
  { id: "legal", icon: Scale, label: "תנאים ופרטיות", href: "/legal" },
  { id: "about", icon: Info, label: "אודות", href: "/about" },
];

interface AppNavigationProps {
  children: React.ReactNode;
}

export function AppNavigation({ children }: AppNavigationProps) {
  const pathname = usePathname();

  // Hide mobile navigation on create-receipt page for better UX
  const hideMobileNav = pathname === "/create-receipt";

  return (
    <div
      className={cn(
        "relative h-full bg-white",
        "lg:pb-0",
        hideMobileNav ? "" : "pb-20 lg:pb-0",
      )}
      dir="rtl"
    >
      {/* Main Content */}
      <main className="h-full">{children}</main>

      {/* Navigation - Bottom for Mobile, Left Sidebar for Desktop */}
      <nav
        className={cn(
          "isolate z-[100]",
          "fixed right-0 bottom-0 left-0 border-t border-neutral-200 bg-white",
          "vercel-trigger lg:fixed lg:start-0 lg:top-0 lg:bottom-0 lg:w-16 lg:flex-col lg:items-center lg:border-e lg:border-t-0 lg:bg-white lg:py-6",
          hideMobileNav && "hidden lg:flex",
        )}
      >
        <div
          className={cn(
            "mx-auto flex max-w-md items-center justify-around px-2 py-2",
            "lg:mx-0 lg:h-full lg:w-full lg:max-w-none lg:flex-col lg:gap-4 lg:overflow-y-auto lg:py-4",
          )}
        >
          {/* Desktop items - shown on lg screens */}
          <div className="hidden lg:flex lg:w-full lg:flex-col lg:items-stretch lg:gap-2 lg:overflow-y-auto lg:px-0">
            {/* Main Navigation */}
            <div className="flex w-full flex-col items-stretch gap-1">
              {navItems.map((item) => {
                const isActive = item.href
                  ? pathname === item.href ||
                    (item.id === "invoices" && pathname === "/create-receipt")
                  : false;

                const navContent = (
                  <>
                    <item.icon
                      className={cn(
                        "h-6 w-6",
                        isActive && !item.comingSoon && "stroke-[2px]",
                      )}
                      strokeWidth={isActive && !item.comingSoon ? 2 : 1.5}
                    />
                    <span
                      className={cn(
                        "relative text-[10px] font-medium whitespace-nowrap",
                        item.comingSoon && "text-neutral-300",
                      )}
                    >
                      {item.label}
                      {item.comingSoon && (
                        <span
                          className="pointer-events-none absolute -inset-x-4 -top-8 -bottom-1 flex items-center justify-center text-xs font-bold tracking-wider text-neutral-900 uppercase"
                          style={{
                            transform: "rotate(-15deg)",
                            textShadow:
                              "0 0 0px white, 0 0 3px white, 0 0 6px white, 0 0 10px white",
                          }}
                        >
                          בקרוב
                        </span>
                      )}
                    </span>
                  </>
                );

                const navClassName = cn(
                  "transition-colors active:scale-92 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:ring-offset-1",
                  "flex flex-col items-center gap-1 rounded-none border border-transparent hover:border-neutral-300",
                  isActive
                    ? "text-neutral-900 border-neutral-300 bg-neutral-50 lg:border-s-0 lg:-me-px py-3 px-0"
                    : "text-neutral-600 hover:text-neutral-800 hover:border-neutral-200 py-3 px-2",
                  item.comingSoon &&
                    "text-neutral-300 cursor-not-allowed py-3 px-2",
                );

                if (item.href && !item.comingSoon) {
                  return (
                    <Link
                      key={item.id}
                      href={item.href}
                      className={navClassName}
                    >
                      <div className="flex flex-col items-center gap-1">
                        {navContent}
                      </div>
                    </Link>
                  );
                }

                return (
                  <button
                    key={item.id}
                    disabled={item.comingSoon}
                    className={navClassName}
                  >
                    {navContent}
                  </button>
                );
              })}
            </div>

            {/* Secondary Navigation (Legal/Info) */}
            <div className="flex w-full flex-col items-stretch gap-1">
              {secondaryNavItems.map((item) => {
                const isActive = pathname === item.href;

                const navClassName = cn(
                  "transition-colors active:scale-92 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:ring-offset-1",
                  "flex flex-col items-center gap-1 rounded-none border border-transparent hover:border-neutral-300 py-2",
                  isActive
                    ? "text-neutral-900 border-neutral-300 bg-neutral-50 lg:border-s-0 lg:-me-px px-0"
                    : "text-neutral-500 hover:text-neutral-700 hover:border-neutral-200 px-2",
                );

                return (
                  <Link key={item.id} href={item.href} className={navClassName}>
                    <div className="flex flex-col items-center gap-1">
                      <item.icon
                        className={cn("h-5 w-5", isActive && "stroke-[2px]")}
                        strokeWidth={isActive ? 2 : 1.5}
                      />
                      <span className="text-[9px] font-medium whitespace-nowrap">
                        {item.label}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Mobile items - shown below lg screens */}
          <div className="contents lg:hidden">
            {/* Navigation Items: Verification, Receipts, API, Shopify */}
            {navItems.map((item) => {
              const isActive = item.href
                ? pathname === item.href ||
                  (item.id === "invoices" && pathname === "/create-receipt")
                : false;

              const navContent = (
                <>
                  <item.icon
                    className={cn(
                      "h-5 min-h-[20px] w-5 min-w-[20px]",
                      isActive && "stroke-[2px]",
                    )}
                    strokeWidth={isActive ? 2 : 1.5}
                  />
                  <span className="relative text-[11px] font-medium whitespace-nowrap">
                    {item.label}
                  </span>
                </>
              );

              const navClassName = cn(
                "flex flex-col items-center gap-1 rounded-none px-3 py-2 min-w-[44px] min-h-[44px] border border-transparent",
                "transition-colors active:scale-92 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:ring-offset-1",
                isActive
                  ? "text-neutral-900 border-neutral-300 bg-neutral-50"
                  : "text-neutral-600 hover:text-neutral-800 hover:border-neutral-200",
              );

              if (item.href && !item.comingSoon) {
                return (
                  <Link key={item.id} href={item.href} className={navClassName}>
                    <div className="flex flex-col items-center gap-1">
                      {navContent}
                    </div>
                  </Link>
                );
              }

              return null; // Skip coming soon items in mobile nav
            })}
          </div>
        </div>
      </nav>
    </div>
  );
}
